<template>
  <div class="org-dept-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>组织架构（部门）</span>
          <div class="header-actions">
            <el-button type="primary" @click="openCreateRoot">新增一级部门</el-button>
            <el-button @click="loadTree">刷新</el-button>
          </div>
        </div>
      </template>

      <el-row :gutter="16">
        <el-col :span="10">
          <div class="panel-title">部门树</div>
          <el-tree
            v-loading="loading"
            :data="deptTree"
            node-key="id"
            :props="treeProps"
            highlight-current
            default-expand-all
            @node-click="handleNodeClick"
          >
            <template #default="{ data }">
              <div class="tree-node">
                <span class="node-name">
                  {{ data.name }}
                  <el-tag v-if="data.status === 0" size="small" type="info" class="ml-6">禁用</el-tag>
                </span>
                <span class="node-actions">
                  <el-button link type="primary" size="small" @click.stop="openCreateChild(data)">新增</el-button>
                  <el-button link type="warning" size="small" @click.stop="openEdit(data)">编辑</el-button>
                  <el-button link type="danger" size="small" @click.stop="handleDelete(data)">删除</el-button>
                </span>
              </div>
            </template>
          </el-tree>
        </el-col>

        <el-col :span="14">
          <div class="panel-title">部门用户绑定（主部门）</div>
          <el-alert type="info" :closable="false" show-icon class="mb-12">
            这里用于给企业账号绑定“主部门”。后续数据范围可按“本人/本部门/本企业”扩展；未绑定部门不影响现有功能。
          </el-alert>

          <el-form :inline="true" class="bind-form">
            <el-form-item label="当前部门">
              <el-input :value="currentDept?.name || '未选择'" disabled style="width: 220px" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :disabled="!currentDept" @click="openBindDialog">绑定用户主部门</el-button>
            </el-form-item>
          </el-form>

          <el-divider />

          <el-form :inline="true" class="filter-form">
            <el-form-item label="按部门筛选用户">
              <el-switch v-model="useDeptFilter" />
            </el-form-item>
            <el-form-item v-if="useDeptFilter" label="deptId">
              <el-input-number v-model="deptFilterId" :min="1" :step="1" />
            </el-form-item>
            <el-form-item>
              <el-button @click="loadCompanyUsers">加载企业用户</el-button>
            </el-form-item>
          </el-form>

          <el-table :data="companyUsers" border style="width: 100%" v-loading="usersLoading">
            <el-table-column prop="id" label="ID" width="90" />
            <el-table-column prop="username" label="用户名" width="140" />
            <el-table-column prop="realName" label="真实姓名" width="140" />
            <el-table-column prop="roleName" label="角色" width="140" />
            <el-table-column prop="status" label="状态" width="90">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'danger'">
                  {{ row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
    </el-card>

    <el-dialog v-model="deptDialogVisible" :title="deptDialogTitle" width="520px" @close="resetDeptForm">
      <el-form ref="deptFormRef" :model="deptForm" :rules="deptRules" label-width="110px">
        <el-form-item label="父部门ID" prop="parentId">
          <el-input-number v-model="deptForm.parentId" :min="0" :step="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="部门名称" prop="name">
          <el-input v-model="deptForm.name" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="deptForm.sort" :min="0" :step="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="deptForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="deptDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitDept">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="bindDialogVisible" title="绑定用户主部门" width="520px" @close="resetBindForm">
      <el-form ref="bindFormRef" :model="bindForm" :rules="bindRules" label-width="110px">
        <el-form-item label="部门">
          <el-input :value="currentDept?.name || ''" disabled />
        </el-form-item>
        <el-form-item label="选择用户" prop="userId">
          <el-select v-model="bindForm.userId" filterable placeholder="选择企业用户" style="width: 100%">
            <el-option
              v-for="u in bindUserOptions"
              :key="u.id"
              :label="`${u.realName || u.username}（${u.roleName || u.userType || '-'}）`"
              :value="u.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="bindDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!currentDept" @click="submitBind">绑定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getDeptTree, createDept, updateDept, deleteDept, bindUserPrimaryDept } from '../api/orgDept'
import { getUserList } from '../api/managerUser'

const loading = ref(false)
const deptTree = ref([])
const currentDept = ref(null)

const treeProps = {
  label: 'name',
  children: 'children'
}

const loadTree = async () => {
  loading.value = true
  try {
    const res = await getDeptTree()
    if (res.code === '000000') {
      deptTree.value = res.data || []
    } else {
      ElMessage.error(res.message || '加载部门树失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('加载部门树失败')
  } finally {
    loading.value = false
  }
}

const handleNodeClick = (node) => {
  currentDept.value = node
}

// 部门CRUD弹窗
const deptDialogVisible = ref(false)
const deptDialogTitle = ref('新增部门')
const deptFormRef = ref(null)
const deptForm = reactive({
  id: null,
  parentId: 0,
  name: '',
  sort: 0,
  status: 1
})

const deptRules = {
  parentId: [{ required: true, message: '请输入父部门ID（根为0）', trigger: 'blur' }],
  name: [{ required: true, message: '请输入部门名称', trigger: 'blur' }]
}

const resetDeptForm = () => {
  deptFormRef.value?.resetFields()
}

const openCreateRoot = () => {
  deptDialogTitle.value = '新增一级部门'
  Object.assign(deptForm, { id: null, parentId: 0, name: '', sort: 0, status: 1 })
  deptDialogVisible.value = true
}

const openCreateChild = (parent) => () => {
  deptDialogTitle.value = `新增子部门（父：${parent.name}）`
  Object.assign(deptForm, { id: null, parentId: parent.id, name: '', sort: 0, status: 1 })
  deptDialogVisible.value = true
}

const openEdit = (dept) => {
  deptDialogTitle.value = `编辑部门（${dept.name}）`
  Object.assign(deptForm, { id: dept.id, parentId: dept.parentId ?? 0, name: dept.name, sort: dept.sort ?? 0, status: dept.status ?? 1 })
  deptDialogVisible.value = true
}

const submitDept = async () => {
  if (!deptFormRef.value) return
  await deptFormRef.value.validate(async (valid) => {
    if (!valid) return
    try {
      const res = deptForm.id ? await updateDept({ ...deptForm }) : await createDept({ ...deptForm })
      if (res.code === '000000') {
        ElMessage.success('保存成功')
        deptDialogVisible.value = false
        loadTree()
      } else {
        ElMessage.error(res.message || '保存失败')
      }
    } catch (e) {
      console.error(e)
      ElMessage.error('保存失败')
    }
  })
}

const handleDelete = async (dept) => {
  try {
    await ElMessageBox.confirm(`确定删除部门「${dept.name}」吗？`, '提示', { type: 'warning' })
    const res = await deleteDept(dept.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      if (currentDept.value?.id === dept.id) currentDept.value = null
      loadTree()
    } else {
      ElMessage.error(res.message || '删除失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      console.error(e)
      ElMessage.error('删除失败')
    }
  }
}

// 企业用户列表（复用 /manager/user/list）
const usersLoading = ref(false)
const companyUsers = ref([])
const useDeptFilter = ref(false)
const deptFilterId = ref(null)

const loadCompanyUsers = async () => {
  usersLoading.value = true
  try {
    const params = { pageNum: 1, pageSize: 1000 }
    if (useDeptFilter.value && deptFilterId.value) {
      params.deptId = deptFilterId.value
    }
    const res = await getUserList(params)
    if (res.code === '000000') {
      companyUsers.value = res.data.records || []
    } else {
      ElMessage.error(res.message || '加载企业用户失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('加载企业用户失败')
  } finally {
    usersLoading.value = false
  }
}

const bindDialogVisible = ref(false)
const bindFormRef = ref(null)
const bindForm = reactive({
  userId: null
})
const bindRules = {
  userId: [{ required: true, message: '请选择用户', trigger: 'change' }]
}

const resetBindForm = () => {
  bindFormRef.value?.resetFields()
}

const bindUserOptions = computed(() => companyUsers.value.filter((u) => u.status === 1))

const openBindDialog = async () => {
  if (!currentDept.value) return
  if (!companyUsers.value.length) {
    await loadCompanyUsers()
  }
  bindDialogVisible.value = true
}

const submitBind = async () => {
  if (!bindFormRef.value || !currentDept.value) return
  await bindFormRef.value.validate(async (valid) => {
    if (!valid) return
    try {
      const res = await bindUserPrimaryDept({ userId: bindForm.userId, deptId: currentDept.value.id })
      if (res.code === '000000') {
        ElMessage.success('绑定成功')
        bindDialogVisible.value = false
      } else {
        ElMessage.error(res.message || '绑定失败')
      }
    } catch (e) {
      console.error(e)
      ElMessage.error('绑定失败')
    }
  })
}

onMounted(() => {
  loadTree()
})
</script>

<style scoped>
.org-dept-management {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.panel-title {
  font-weight: 600;
  margin-bottom: 10px;
}
.tree-node {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}
.node-actions {
  display: inline-flex;
  gap: 6px;
}
.ml-6 {
  margin-left: 6px;
}
.mb-12 {
  margin-bottom: 12px;
}
.bind-form,
.filter-form {
  margin-top: 8px;
}
</style>

